import { Badge, Box, Button, Flex, HStack, Text, useCustomToast, VStack } from '@elkaso-app/web-design';
import { getFamilyPriceMatrixEndpointIdentifier } from 'apis/exclusive-deals/use-get-family-price-matrix-api';
import {
  PriceImportCommitResult,
  priceImportRowsEndpointIdentifier,
  PriceImportRun,
  priceImportsEndpointIdentifier,
  useCommitPriceImportRunApi,
} from 'apis/price-imports';
import { Can } from 'configs/can';
import { commitPreflight } from 'pages/price-imports/utils/run-status';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

export interface CommitPanelProps {
  run: PriceImportRun;
}

/**
 * The commit gate.
 *
 * Undecided rows do not block — they are skipped — but the count is stated
 * plainly, because silently dropping 71 of a supplier's lines is exactly what
 * gets discovered a week later.
 */
export const CommitPanel = ({ run }: CommitPanelProps) => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const [result, setResult] = useState<PriceImportCommitResult | null>(null);

  const { mutateAsync: commitRun, isLoading } = useCommitPriceImportRunApi();
  const preflight = commitPreflight(run);

  const handleCommit = async () => {
    try {
      const response = await commitRun({ runId: run.id });
      setResult(response.data);

      // Every surface that reads a price the import just wrote.
      queryClient.invalidateQueries([getFamilyPriceMatrixEndpointIdentifier]);
      queryClient.invalidateQueries([priceImportsEndpointIdentifier, run.id]);
      queryClient.invalidateQueries([priceImportRowsEndpointIdentifier, run.id]);
      queryClient.invalidateQueries([priceImportsEndpointIdentifier]);

      toast({
        description:
          response.data.failed > 0
            ? `${response.data.committed} prices written, ${response.data.failed} failed. Re-run to retry the rest.`
            : `${response.data.committed} prices written`,
        status: response.data.failed > 0 ? 'error' : 'success',
      });
    } catch {
      // The hook already surfaced the message.
    }
  };

  if (result) {
    return (
      <Box border='1px solid' borderColor='gray.200' borderRadius='8px' p='16px'>
        <Text fontSize='md' fontWeight='semibold' mb='8px'>
          Import committed
        </Text>
        <VStack align='stretch' spacing='4px' fontSize='sm'>
          <Text>{result.committed} prices written.</Text>
          {result.failed > 0 && (
            <Text color='orange.700'>
              {result.failed} rows failed and {result.remaining} are still waiting. Committing again retries them — rows
              that already landed are skipped.
            </Text>
          )}
          {result.createdFamilyIds.length > 0 && <Text>{result.createdFamilyIds.length} new families created.</Text>}
          {result.createdMasterProductIds.length > 0 && (
            <Text>{result.createdMasterProductIds.length} new products created.</Text>
          )}
          {result.createdBrandIds.length > 0 && <Text>{result.createdBrandIds.length} new brands created.</Text>}
          {result.createdAttributeValueIds.length > 0 && (
            <Text>{result.createdAttributeValueIds.length} new attribute values created.</Text>
          )}
        </VStack>

        {result.errors.length > 0 && (
          <Box mt='12px' bg='orange.50' borderRadius='6px' p='10px'>
            {result.errors.slice(0, 5).map((error) => (
              <Text key={`${error.rowNumber}-${error.reason}`} fontSize='xs' color='orange.800'>
                Row {error.rowNumber}: {error.reason}
              </Text>
            ))}
            {result.errors.length > 5 && (
              <Text fontSize='xs' color='orange.700' mt='4px'>
                …and {result.errors.length - 5} more.
              </Text>
            )}
          </Box>
        )}

        <Button as='a' href='/app/prices/list' size='sm' colorScheme='red' mt='12px'>
          Open the Prices matrix
        </Button>
      </Box>
    );
  }

  return (
    <Box border='1px solid' borderColor='gray.200' borderRadius='8px' p='16px' maxW='620px'>
      <Text fontSize='md' fontWeight='semibold' mb='12px'>
        Ready to commit?
      </Text>

      <VStack align='stretch' spacing='8px' mb='16px'>
        <Flex justify='space-between'>
          <Text fontSize='sm'>Prices that will be written</Text>
          <Badge colorScheme='green' variant='subtle'>
            {run.matchedRows + run.proposalRows}
          </Badge>
        </Flex>
        <Flex justify='space-between'>
          <Text fontSize='sm'>New catalogue entries proposed</Text>
          <Badge colorScheme='purple' variant='subtle'>
            {run.proposalRows}
          </Badge>
        </Flex>
        {run.committedRows > 0 && (
          <Flex justify='space-between'>
            <Text fontSize='sm' color='gray.600'>
              Already committed (will be skipped)
            </Text>
            <Badge variant='subtle'>{run.committedRows}</Badge>
          </Flex>
        )}
      </VStack>

      {preflight.warnings.map((warning) => (
        <Box key={warning} bg='orange.50' borderRadius='6px' p='10px' mb='8px'>
          <Text fontSize='sm' color='orange.800'>
            {warning}
          </Text>
        </Box>
      ))}

      {preflight.blockingReason && (
        <Box bg='gray.50' borderRadius='6px' p='10px' mb='8px'>
          <Text fontSize='sm' color='gray.700'>
            {preflight.blockingReason}
          </Text>
        </Box>
      )}

      <HStack>
        <Can I='manage' a='all'>
          <Button
            colorScheme='red'
            size='sm'
            isLoading={isLoading}
            isDisabled={!preflight.canCommit}
            onClick={handleCommit}>
            Commit {run.matchedRows + run.proposalRows} prices
          </Button>
        </Can>
      </HStack>
    </Box>
  );
};
